import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
  computed,
  input,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatChipsModule } from '@angular/material/chips';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { EmojiSelector } from '@components/emoji-selector/emoji-selector.component';
import { PuebloBase } from '@components/pueblo-base.component';
import { isDefined } from '@utils/rxjs-utils';
import { Database as EmojiDatabase } from 'emoji-picker-element';
import { NativeEmoji } from 'emoji-picker-element/shared';
import { debounceTime, defer, filter, of, switchMap } from 'rxjs';
import { IHangForm } from '../form-types';
import { LocationForm } from '../location/location-form.component';

// TODO: Add back sub-options when implementation is ready
export enum SubOption {
  TITLE = 'title',
  // DURATION = 'duration',
  LOCATION = 'location',
  LINK = 'link',
  DETAILS = 'details',
  // MAX_SPOTS = 'maxSpots',
  // MIN_SPOTS = 'minSpots',
}

@Component({
  selector: 'suboption-form',
  templateUrl: 'suboption-form.component.html',
  styleUrl: 'suboption-form.component.scss',
  imports: [
    EmojiSelector,
    MatButtonModule,
    LocationForm,
    MatChipsModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatMenuModule,
    MatProgressSpinnerModule,
    NgTemplateOutlet,
    ReactiveFormsModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SubOptionForm extends PuebloBase implements OnInit {
  private readonly emojiDatabase = new EmojiDatabase();

  @ViewChild(MatMenuTrigger) trigger?: MatMenuTrigger;

  protected isOptionsCollapsed = false;

  @Input({ required: true }) form!: FormGroup<IHangForm>;
  enabledOptions = input<SubOption[]>([]);
  initialOptionsVisible = input<SubOption[]>([]);
  alwaysVisibleSubOptions = input(new Set<SubOption>([]), {
    transform: (value: SubOption[]) => new Set(value),
  });

  @Output() removed = new EventEmitter<number>();

  protected readonly SubOption = SubOption;
  protected readonly subOptions = Object.values(SubOption);
  protected readonly $selectedSubOptions = signal<SubOption[]>([]);
  protected readonly $enabledSubOptions = computed<SubOption[]>(() => {
    const enabledOptionsInput = this.enabledOptions();
    const subOptions = [...Object.values(SubOption)];
    if (!enabledOptionsInput.length) return subOptions;
    return subOptions.filter((subOption) =>
      enabledOptionsInput.includes(subOption),
    );
  });
  protected readonly $enabledSelectedSubOptions = computed<SubOption[]>(() => {
    const enabledOptionsInput = this.enabledOptions();
    const selectedSubOptions = this.$selectedSubOptions();
    if (!enabledOptionsInput.length) return selectedSubOptions;
    return selectedSubOptions.filter((subOption) =>
      enabledOptionsInput.includes(subOption),
    );
  });
  private readonly emojiMenuTrigger = viewChild.required(MatMenuTrigger);

  protected readonly $emojiManuallyChanged = signal(false);

  ngOnInit(): void {
    if (this.form.controls.link.value) {
      this.$selectedSubOptions.update((options) => [
        ...options,
        SubOption.LINK,
      ]);
    }

    if (this.form.controls.details.value) {
      this.$selectedSubOptions.update((options) => [
        ...options,
        SubOption.DETAILS,
      ]);
    }

    this.form.controls.title.valueChanges
      .pipe(
        filter(() => !this.$emojiManuallyChanged()),
        filter(isDefined),
        debounceTime(200),
        filter((value) => !!value),
        switchMap((value) =>
          defer(() => this.emojiDatabase.getEmojiBySearchQuery(value)).pipe(
            switchMap((emojiResults) => {
              if (emojiResults.length) {
                const emoji = emojiResults.filter(
                  (result): result is NativeEmoji =>
                    result.hasOwnProperty('unicode'),
                )[0];
                return of(emoji.unicode);
              }
              return this.pbClient.generateEmoji(value);
            }),
          ),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((emoji) => {
        this.form.controls.emoji.setValue(emoji);
        this.changeDetectorRef.markForCheck();
      });

    this.$selectedSubOptions.set(this.initialOptionsVisible());
  }

  protected toggleCollapse(): void {
    this.isOptionsCollapsed = !this.isOptionsCollapsed;
  }

  protected showSubOption(subOption: SubOption): void {
    this.$selectedSubOptions.update((options) => [...options, subOption]);
  }

  protected hideSubOption(subOption: SubOption): void {
    this.$selectedSubOptions.set(
      this.$selectedSubOptions().filter((option) => option !== subOption),
    );

    const subOptionForm = this.form.get(subOption);
    if (subOptionForm) {
      subOptionForm.reset();
    } else {
      throw new TypeError(`${subOption} is not a valid form control`);
    }
  }

  protected formatChipLabel(label: string): string {
    const result = label.replace(/([A-Z])/g, ' $1');
    return result.charAt(0).toUpperCase() + result.slice(1);
  }

  protected getSuboptionIcon(suboption: SubOption): string {
    switch (suboption) {
      case SubOption.TITLE:
        return 'title';
      case SubOption.LOCATION:
        return 'location_on';
      case SubOption.LINK:
        return 'link';
      case SubOption.DETAILS:
        return 'notes';
    }
  }

  protected getSuboptionChipLabel(suboption: SubOption): string {
    switch (suboption) {
      case SubOption.TITLE:
        return 'Title';
      case SubOption.LOCATION:
        return 'Location';
      case SubOption.LINK:
        return 'Link';
      case SubOption.DETAILS:
        return 'Description';
    }
  }

  protected changeEmoji(emoji: string): void {
    this.form.controls.emoji.setValue(emoji);
    this.$emojiManuallyChanged.set(true);
    this.emojiMenuTrigger().closeMenu();
  }

  protected handleTitleClick(inputRef: HTMLInputElement) {
    (inputRef as HTMLInputElement).select();
  }
}
